import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Button,
  Alert,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Chip,
  Paper,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Message as MessageIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';

const MessageDetailsScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { username } = location.state || {};
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [smsText, setSmsText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [selectedFile, setSelectedFile] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!username) {
      navigate('/messages');
      return;
    }
    loadData();
  }, [username]);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [items]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `api/school-common/message/get-transacted-messages?username=${username}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setItems(response.data || []);
    } catch (error) {
      console.error('MessageDetailsScreen loadData error:', error);
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    await loadData();
  };

  const confirmDelete = (id) => {
    setDeleteItemId(id);
    setDeleteDialogOpen(true);
  };

  const deleteItem = async () => {
    if (!deleteItemId) return;

    try {
      setDeletingId(deleteItemId);
      setDeleteDialogOpen(false);
      const token = localStorage.getItem('userToken');

      await api.delete(`api/school-common/message/delete-message?id=${deleteItemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Remove item from local state
      setItems((prevItems) => prevItems.filter((item) => item.id !== deleteItemId));
      setNotification({ open: true, message: 'Message deleted successfully', severity: 'success' });
    } catch (error) {
      console.error('MessageDetailsScreen deleteItem error:', error);
      handleCatch(error);
    } finally {
      setDeletingId(null);
      setDeleteItemId(null);
    }
  };

  const sendMessage = async () => {
    if (!username) {
      setNotification({ open: true, message: 'Username is required to send message!', severity: 'error' });
      return;
    }
    if (!smsText?.trim()) {
      setNotification({ open: true, message: 'Message required!', severity: 'error' });
      return;
    }

    try {
      setSendingMessage(true);
      const token = localStorage.getItem('userToken');
      const formData = new FormData();
      formData.append('ToUserName', username);
      formData.append('Description', smsText.trim());
      
      if (selectedFile) {
        formData.append('File1', selectedFile);
      }

      await api.post('api/school-common/message/send-message-to-anyone', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setNotification({ open: true, message: `Message sent to ${username}`, severity: 'success' });
      setSmsText('');
      setSelectedFile(null);
      await loadData();
    } catch (error) {
      console.error('MessageDetailsScreen sendMessage error:', error);
      handleCatch(error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file size (limit to 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setNotification({ open: true, message: 'File size must be less than 10MB', severity: 'error' });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleViewAttachment = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleDownloadAttachment = async (url, filename) => {
    if (!url) return;
    
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download error:', error);
      setNotification({ open: true, message: 'Failed to download attachment', severity: 'error' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Parse UTC date string and convert to local time
    // Backend sends DateTime.UtcNow which is serialized as UTC
    // Ensure it's treated as UTC by appending 'Z' if not present
    let dateStr = dateString;
    if (dateStr && !dateStr.endsWith('Z') && !dateStr.includes('+')) {
      dateStr = dateStr + 'Z';
    }
    
    const date = new Date(dateStr);
    const now = new Date();
    
    // Calculate time difference in milliseconds
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    // Get calendar dates (ignoring time) for accurate day comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((today - messageDate) / (1000 * 60 * 60 * 24));
    
    // Just now (less than 1 minute)
    if (diffMins < 1) {
      return 'Just now';
    }
    
    // Minutes ago (1-59 minutes)
    if (diffMins < 60) {
      return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    }
    
    // Hours ago (1-23 hours, but same calendar day)
    if (diffHours < 24 && diffDays === 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    }
    
    // Today (more than 24 hours but same calendar day)
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    // Yesterday
    if (diffDays === 1) {
      return 'Yesterday ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    // This week (less than 7 days)
    if (diffDays < 7) {
      return (
        date.toLocaleDateString([], { weekday: 'short' }) +
        ' ' +
        date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      );
    }
    
    // Older messages
    return (
      date.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ' ' +
      date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return 'attachment';
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.substring(pathname.lastIndexOf('/') + 1);
      return decodeURIComponent(filename);
    } catch {
      return 'attachment';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <MobileHeader
        title={username || 'Messages'}
        onBack={() => navigate('/messages')}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: '#EBEBEB',
          px: 1.5,
          py: 1.5,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, py: 6 }}>
            <MessageIcon sx={{ fontSize: 56, color: '#ccc', mb: 2 }} />
            <Typography sx={{ fontWeight: 600, color: '#888', fontSize: 15 }}>No Messages Yet</Typography>
            <Typography sx={{ fontSize: 12, color: '#bbb', mt: 0.5 }}>Start a conversation with {username}.</Typography>
          </Box>
        ) : (
          <>
            {items.map((item) => {
              const isReceived = item.type === 'R';
              const hasAttachment = item.file1;
              return (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    justifyContent: isReceived ? 'flex-start' : 'flex-end',
                    mb: 1,
                  }}
                >
                  {isReceived && (
                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#ccc', mr: 0.75, mt: 0.5, flexShrink: 0 }}>
                      <PersonIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                  )}
                  <Box
                    sx={{
                      maxWidth: '75%',
                      bgcolor: isReceived ? '#fff' : colors.BLUE,
                      color: isReceived ? '#222' : '#fff',
                      borderRadius: isReceived ? '2px 14px 14px 14px' : '14px 2px 14px 14px',
                      px: 1.5,
                      pt: 1,
                      pb: 0.75,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    }}
                  >
                    <Typography sx={{ fontSize: 14, lineHeight: 1.4 }}>{item.description}</Typography>
                    
                    {hasAttachment && (
                      <Paper
                        elevation={0}
                        sx={{
                          mt: 1,
                          p: 1,
                          borderRadius: 1.5,
                          bgcolor: isReceived ? '#f5f5f5' : 'rgba(255,255,255,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <AttachFileIcon 
                          sx={{ 
                            fontSize: 18, 
                            color: isReceived ? colors.BLUE : '#fff',
                            transform: 'rotate(-45deg)'
                          }} 
                        />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            flex: 1, 
                            fontSize: 12,
                            color: isReceived ? '#555' : '#fff',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }} 
                          noWrap
                        >
                          {getFileNameFromUrl(item.file1)}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewAttachment(item.file1)}
                          sx={{ 
                            color: isReceived ? colors.BLUE : '#fff',
                            p: 0.5 
                          }}
                        >
                          <VisibilityIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDownloadAttachment(item.file1, getFileNameFromUrl(item.file1))}
                          sx={{ 
                            color: isReceived ? colors.BLUE : '#fff',
                            p: 0.5 
                          }}
                        >
                          <DownloadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Paper>
                    )}
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 0.5, gap: 0.5 }}>
                      <Typography sx={{ fontSize: 10, color: isReceived ? '#aaa' : 'rgba(255,255,255,0.6)' }}>
                        {formatDate(item.updatedDate)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => confirmDelete(item.id)}
                        disabled={deletingId === item.id}
                        sx={{ p: 0, color: isReceived ? '#ccc' : 'rgba(255,255,255,0.5)' }}
                      >
                        {deletingId === item.id
                          ? <CircularProgress size={10} sx={{ color: 'inherit' }} />
                          : <DeleteIcon sx={{ fontSize: 12 }} />}
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Input Bar */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: '#fff',
          borderTop: '1px solid #eee',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* File attachment display */}
        {selectedFile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={<AttachFileIcon sx={{ fontSize: 16 }} />}
              label={selectedFile.name}
              onDelete={handleRemoveFile}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ maxWidth: '80%' }}
            />
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            accept="*/*"
            style={{ display: 'none' }}
            id="message-file-upload"
            type="file"
            onChange={handleFileSelect}
          />
          
          {/* Attach file button */}
          <label htmlFor="message-file-upload">
            <IconButton 
              component="span" 
              disabled={sendingMessage}
              sx={{ 
                color: colors.BLUE,
                mb: 0.25 
              }}
            >
              <AttachFileIcon />
            </IconButton>
          </label>

          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type a message..."
            value={smsText}
            onChange={(e) => setSmsText(e.target.value)}
            disabled={sendingMessage}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: '#f8f8f8',
                fontSize: 14,
              },
            }}
          />
          <Box
            component="button"
            onClick={sendMessage}
            disabled={!smsText?.trim() || sendingMessage}
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              bgcolor: smsText?.trim() && !sendingMessage ? colors.BLUE : '#ccc',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: smsText?.trim() && !sendingMessage ? 'pointer' : 'default',
              flexShrink: 0,
              mb: 0.25,
            }}
          >
            {sendingMessage
              ? <CircularProgress size={20} sx={{ color: '#fff' }} />
              : <SendIcon sx={{ color: '#fff', fontSize: 20 }} />}
          </Box>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Message</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this message?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={deleteItem} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} variant="filled" sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MessageDetailsScreen;
      {/* Header */}
